"use client";

import PublicCreation from "@/app/home/[locale]/(landingnew)/PublicCreation";
import { useAppContext } from "@/context/AppContext";
import { useHash } from "@/hooks/useHash";
import { cn } from "@/lib/utils";
import { useEffect, useMemo, useRef, useState } from "react";
import { Trans } from "react-i18next";
import useSWR from "swr";

export default function Hero() {
  const { locale } = useAppContext();
  const pageHash = useHash();
  const [currState, setCurrState] = useState("main");
  const [isAutoPlay, setIsAutoPlay] = useState(false);
  const timeoutRef = useRef<any>(null);

  const landingPreview = useMemo(() => {
    const pl = pageHash.split("#pl-");
    if (pl.length > 0) {
      return pl[1];
    }
    return null;
  }, [pageHash]);

  const landingConfig = useSWR(
    () => "/landing" + (landingPreview ? "?preview_id=" + landingPreview : ""),
    {
      shouldRetryOnError: false,
    }
  );

  useEffect(() => {
    if (landingConfig.data?.data && landingConfig.data?.data?.is_featured) {
      setCurrState("featured");
      setIsAutoPlay(true);
      timeoutRef.current = setTimeout(() => {
        setIsAutoPlay(false);
        setCurrState("main");
      }, 6000);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [landingConfig.data]);

  const hasFeatured =
    landingConfig.data?.data && landingConfig.data?.data?.is_featured;

  const isCustomBg =
    landingConfig.data?.data && !landingConfig.data?.data?.is_featured;

  const inverse =
    landingConfig.data?.data &&
    !landingConfig.data?.data?.is_featured &&
    landingConfig.data?.data?.inverse;

  // console.log(landingConfig.data);

  return (
    <section className="relative mx-auto px-3 w-full max-w-[100rem]">
      <div className="relative flex flex-col justify-center items-center px-4 rounded-3xl min-h-[40rem] lg:min-h-[50rem] overflow-hidden">
        {currState === "featured" && (
          <div>
            <div
              className="absolute inset-0 flex justify-center items-center bg-secondary bg-cover bg-center px-5 pt-10 pb-20"
              style={{
                backgroundColor: landingConfig?.data?.data?.background_color,
                backgroundImage: `url(${landingConfig?.data?.data?.background})`,
              }}
            >
              <a
                href={
                  locale === "id"
                    ? landingConfig?.data?.data?.url
                    : landingConfig?.data?.data?.url_en ||
                      landingConfig?.data?.data?.url
                }
                className={cn(
                  "duration-300 transition-all",
                  "z-20 relative pointer-events-auto",
                  "animate-in slide-in-from-bottom-3",
                  "hover:scale-105",
                  "max-w-full max-h-full flex"
                )}
              >
                <img
                  src={
                    locale === "id"
                      ? landingConfig?.data?.data?.src
                      : landingConfig?.data?.data?.src_en ||
                        landingConfig?.data?.data?.src
                  }
                  alt="background"
                  className="max-w-full max-h-full object-contain"
                />
              </a>
            </div>
          </div>
        )}

        <div
          className={cn(
            "flex flex-col duration-300",
            currState === "main" ? "" : "opacity-0 pointer-events-none"
          )}
        >
          <div
            className={cn(
              "-z-10 absolute inset-0 bg-cover bg-center pointer-events-none",
              isCustomBg ? "" : "opacity-40"
            )}
            style={
              isCustomBg
                ? {
                    ...landingConfig.data.data.background_style,
                    backgroundColor:
                      landingConfig?.data?.data?.background_color,
                    backgroundImage: `url(${landingConfig?.data?.data?.background})`,
                  }
                : {
                    backgroundImage: "url('/images/abstract-bg.jpg')",
                  }
            }
          />
          <h1
            className={cn(
              "font-montserrat font-bold text-4xl lg:text-7xl text-center",
              inverse ? "text-white" : ""
            )}
          >
            <Trans i18nKey="landing:home_head">
              #semuabisadi<span className="text-primary">sid</span>
            </Trans>
          </h1>
          <p
            className={cn(
              "mx-auto mt-3 max-w-2xl font-worksans text-lg lg:text-2xl text-center",
              inverse ? "text-white" : "text-foreground/60"
            )}
          >
            <Trans i18nKey="landing:home_subhead">
              Maksimalkan semua kebutuhan campaign, bisnis, dan media sosial
              anda dengan s.id
            </Trans>
          </p>
          <PublicCreation />
        </div>
        {hasFeatured && (
          <div className="bottom-8 z-50 absolute flex justify-center gap-3 w-full pointer-events-none">
            <CarouselPin
              active={currState === "featured"}
              activeAnimate={isAutoPlay}
              onClick={() => setCurrState("featured")}
            />
            <CarouselPin
              active={currState === "main"}
              onClick={() => {
                setIsAutoPlay(false);
                if (timeoutRef.current) {
                  clearTimeout(timeoutRef.current);
                }
                setCurrState("main");
              }}
            />
          </div>
        )}
      </div>
    </section>
  );
}

function CarouselPin({ active, onClick, activeAnimate = false }) {
  return (
    <button
      type="button"
      className={cn(
        "transition-all duration-300",
        "overflow-hidden",
        "rounded-full h-2.5 pointer-events-auto",
        active ? "bg-primary/50 w-20" : "bg-primary/20 w-14"
      )}
      onClick={onClick}
    >
      {active && (
        <div
          className={cn(
            "bg-primary rounded-full h-2.5",
            activeAnimate ? "animate-carousel-pin duration-5000" : "w-full"
          )}
        />
      )}
    </button>
  );
}
